import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Popover,
  Tooltip,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CheckCircleTwoToneIcon from '@material-ui/icons/CheckCircleTwoTone';
import BugReportTwoToneIcon from '@material-ui/icons/BugReportTwoTone';
import SecurityTwoToneIcon from '@material-ui/icons/SecurityTwoTone';
import AssessmentTwoToneIcon from '@material-ui/icons/AssessmentTwoTone';
import AssignmentTurnedInTwoToneIcon from '@material-ui/icons/AssignmentTurnedInTwoTone';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import { ProjectQualityMetricsDTO } from '../../DTO/ProjectMetrics';
import { WeakPointSeverity } from '../../DTO/MarketplaceMetrics';

interface ProjectMetricsDisplayProps {
  metrics: ProjectQualityMetricsDTO;
}

const WEAK_POINT_SEVERITY_COLOR: Record<WeakPointSeverity, string> = {
  low: '#1976d2',
  medium: '#f57c00',
  high: '#d32f2f',
};

const WEAK_POINT_SEVERITY_LABEL: Record<WeakPointSeverity, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

const WEAK_POINT_SEVERITY_ORDER: WeakPointSeverity[] = ['high', 'medium', 'low'];
const PACKAGE_COMPLIANCE_COLOR = {
  low: '#d32f2f',
  medium: '#fbc02d',
  high: '#2e7d32',
};

const StatusIcon: React.FC<{ ok: boolean }> = ({ ok }) =>
  ok ? (
    <CheckCircleIcon fontSize="small" style={{ color: '#2e7d32', marginRight: 4, verticalAlign: 'middle' }} />
  ) : (
    <CancelIcon fontSize="small" style={{ color: '#d32f2f', marginRight: 4, verticalAlign: 'middle' }} />
  );

const ProjectMetricsDisplay: React.FC<ProjectMetricsDisplayProps> = ({ metrics }) => {
  const [weakPointsInfoAnchor, setWeakPointsInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [qualityInfoAnchor, setQualityInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [securityInfoAnchor, setSecurityInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [dependenciesInfoAnchor, setDependenciesInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [agenticInfoAnchor, setAgenticInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [architecturalFitnessInfoAnchor, setArchitecturalFitnessInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [documentationScoreInfoAnchor, setDocumentationScoreInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [packageComplianceInfoAnchor, setPackageComplianceInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [overallComplianceInfoAnchor, setOverallComplianceInfoAnchor] = useState<HTMLButtonElement | null>(null);

  const { coverage, security, dependencies, agenticEvaluator, packageCompliance } = metrics;

  const hasDependencyValues =
    dependencies.hasReport &&
    [dependencies.critical, dependencies.high, dependencies.medium, dependencies.low].some(
      (value) => (value ?? 0) > 0,
    );

  return (
    <Box mt={3}>
      {/* Coverage */}
      <Box display="flex" alignItems="center">
        <CheckCircleTwoToneIcon fontSize="small" style={{ marginRight: 8, marginBottom: 4 }} />
        <Typography variant="h6" gutterBottom>Quality and Security</Typography>
        <IconButton
          aria-label="More information about quality and security"
          size="small"
          style={{ marginLeft: 4, marginBottom: 4 }}
          onClick={(event) => setQualityInfoAnchor(event.currentTarget)}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
        <Popover
          open={Boolean(qualityInfoAnchor)}
          anchorEl={qualityInfoAnchor}
          onClose={() => setQualityInfoAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Typography style={{ padding: 16, maxWidth: 360 }}>
            This section shows the test coverage quality of this project. It reports whether an
            automatic test report is available and whether the measured coverage meets the
            configured threshold.
          </Typography>
        </Popover>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography gutterBottom>Test Coverage</Typography>
              <Typography variant="h4">
                {coverage.hasReport && coverage.value !== null ? `${coverage.value}%` : 'N/A'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {coverage.hasReport ? 'Automatic test report found' : 'No automatic test report found'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography gutterBottom>Coverage Threshold</Typography>
              {coverage.threshold !== null && coverage.satisfiesMinThreshold !== null ? (
                <>
                  <Typography
                    variant="h4"
                    style={{
                      color: coverage.satisfiesMinThreshold ? '#2e7d32' : '#d32f2f',
                    }}
                  >
                    <StatusIcon ok={coverage.satisfiesMinThreshold} />
                    {coverage.satisfiesMinThreshold ? 'Above' : 'Below'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Threshold: {coverage.threshold}%
                  </Typography>
                </>
              ) : (
                <Typography variant="h4">N/A</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Security */}
      <Box display="flex" alignItems="center" style={{ marginTop: 24 }}>
        <SecurityTwoToneIcon fontSize="small" style={{ marginRight: 8, marginBottom: 4  }} />
        <Typography variant="h6" gutterBottom>Security Analysis</Typography>
        <IconButton
          aria-label="More information about security analysis"
          size="small"
          style={{ marginLeft: 4, marginBottom: 4 }}
          onClick={(event) => setSecurityInfoAnchor(event.currentTarget)}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
        <Popover
          open={Boolean(securityInfoAnchor)}
          anchorEl={securityInfoAnchor}
          onClose={() => setSecurityInfoAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Typography style={{ padding: 16, maxWidth: 360 }}>
            This section shows security scanning coverage for this project: whether a SAST
            (Static Application Security Testing) report and a secret detection report are
            available.
          </Typography>
        </Popover>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography gutterBottom>SAST Report</Typography>
              <Typography variant="h4">
                <StatusIcon ok={security.sastReportFound} />
                {security.sastReportFound ? 'Found' : 'Not found'}
                {security.sastReportFound && (
                  <>
                    {', '}{security.sastWarningsCount !== null ? security.sastWarningsCount : 'N/A'} Warnings
                  </>
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography gutterBottom>Secret Detection</Typography>
              <Typography variant="h4">
                <StatusIcon ok={security.secretDetectionReportFound} />
                {security.secretDetectionReportFound ? 'Found' : 'Not found'}
                {security.secretDetectionReportFound && (
                  <>
                    {', '}{security.secretDetectionWarningsCount !== null
                      ? security.secretDetectionWarningsCount
                      : 'N/A'} Warnings
                  </>
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dependencies */}
      <Box display="flex" alignItems="center" style={{ marginTop: 24 }}>
        <BugReportTwoToneIcon fontSize="small" style={{ marginRight: 8, marginBottom: 4 }} />
        <Typography variant="h6" gutterBottom>Dependency Vulnerabilities</Typography>
        <IconButton
          aria-label="More information about dependency vulnerabilities"
          size="small"
          style={{ marginLeft: 4, marginBottom: 4 }}
          onClick={(event) => setDependenciesInfoAnchor(event.currentTarget)}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
        <Popover
          open={Boolean(dependenciesInfoAnchor)}
          anchorEl={dependenciesInfoAnchor}
          onClose={() => setDependenciesInfoAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Typography style={{ padding: 16, maxWidth: 360 }}>
            This section summarises known vulnerabilities found in this project's third-party
            dependencies, broken down by severity (Critical, High, Medium, Low).
          </Typography>
        </Popover>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography gutterBottom>Vulnerabilities by Severity</Typography>
              {dependencies.hasReport ? (
                hasDependencyValues ? (
                  <Box
                    display="flex"
                    alignItems="flex-end"
                    justifyContent="space-around"
                    style={{ minHeight: 220, paddingBottom: 12 }}
                  >
                    {(
                      [
                        { label: 'Critical', key: 'critical', color: '#d32f2f' },
                        { label: 'High', key: 'high', color: '#f57c00' },
                        { label: 'Medium', key: 'medium', color: '#fbc02d' },
                        { label: 'Low', key: 'low', color: '#7cb342' },
                      ] as const
                    ).map(({ label, key, color }) => {
                      const value = dependencies[key] ?? 0;
                      const maxValue = Math.max(
                        dependencies.critical ?? 0,
                        dependencies.high ?? 0,
                        dependencies.medium ?? 0,
                        dependencies.low ?? 0,
                        1,
                      );
                      const barHeight = Math.max((value / maxValue) * 140, value > 0 ? 18 : 0);

                      return (
                        <Box key={key} display="flex" flexDirection="column" alignItems="center" width="22%">
                          <Tooltip title={`${label}: ${value}`}>
                            <Box
                              width="60%"
                              borderRadius={4}
                              style={{ height: barHeight, backgroundColor: color, minHeight: value > 0 ? 18 : 0 }}
                            />
                          </Tooltip>
                          <Typography variant="h4" style={{ marginTop: 8, color }}>{value}</Typography>
                          <Typography variant="body1">{label}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body1" color="textSecondary">No vulnerabilities found</Typography>
                )
              ) : (
                <Typography variant="h4">N/A</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Package Compliance */}
      <Box display="flex" alignItems="center" style={{ marginTop: 24 }}>
        <AssignmentTurnedInTwoToneIcon style={{ marginRight: 8, marginBottom: 4 }} />
        <Typography variant="h6" gutterBottom>Package Compliance</Typography>
        <IconButton
          aria-label="More information about package compliance"
          size="small"
          style={{ marginLeft: 4, marginBottom: 4 }}
          onClick={(event) => setPackageComplianceInfoAnchor(event.currentTarget)}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
        <Popover
          open={Boolean(packageComplianceInfoAnchor)}
          anchorEl={packageComplianceInfoAnchor}
          onClose={() => setPackageComplianceInfoAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Typography style={{ padding: 16, maxWidth: 360 }}>
            This section shows the package compliance assessment, including evaluator errors
            and problems identified in non-compliant findings.
          </Typography>
        </Popover>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography gutterBottom>Overall Project Compliance</Typography>
                <IconButton
                  aria-label="More information about overall project compliance"
                  size="small"
                  onClick={(event) => setOverallComplianceInfoAnchor(event.currentTarget)}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
                <Popover
                  open={Boolean(overallComplianceInfoAnchor)}
                  anchorEl={overallComplianceInfoAnchor}
                  onClose={() => setOverallComplianceInfoAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <Typography style={{ padding: 16, maxWidth: 320 }}>
                    This value is the overall package compliance status for this project: high,
                    medium, or low.
                  </Typography>
                </Popover>
              </Box>
              <Typography
                variant="h4"
                style={{
                  color: packageCompliance.overallProjectCompliance !== null
                    ? PACKAGE_COMPLIANCE_COLOR[packageCompliance.overallProjectCompliance]
                    : undefined,
                }}
              >
                {packageCompliance.overallProjectCompliance ?? 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography gutterBottom>Package Compliance Issues</Typography>
              <Typography variant="h4">
                {packageCompliance.hasReport ? `${packageCompliance.errorsCount} Errors` : 'N/A'} 
              </Typography>
              <Typography variant="h4" style={{ marginTop: 8 }}>
                {packageCompliance.hasReport ? `${packageCompliance.nonCompliantProblemsCount} Non-compliant problems` : ''}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Agentic application assessment */}
      <Box display="flex" alignItems="center" style={{ marginTop: 24 }}>
        <AssessmentTwoToneIcon style={{ marginRight: 8, marginBottom: 4 }} />
        <Typography variant="h6" gutterBottom>Agentic Application Assessment</Typography>
        <IconButton
          aria-label="More information about agentic evaluator"
          size="small"
          style={{ marginLeft: 4, marginBottom: 4 }}
          onClick={(event) => setAgenticInfoAnchor(event.currentTarget)}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
        <Popover
          open={Boolean(agenticInfoAnchor)}
          anchorEl={agenticInfoAnchor}
          onClose={() => setAgenticInfoAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Typography style={{ padding: 16, maxWidth: 360 }}>
            This section presents AI-driven quality assessments performed by the agentic
            evaluator for this project: the Architectural Fitness score, the Documentation
            Score, and a breakdown of identified architectural findings by category and severity.
          </Typography>
        </Popover>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography gutterBottom>Architectural Fitness</Typography>
                  <IconButton
                    aria-label="More information about architectural fitness"
                    size="small"
                    onClick={(event) => setArchitecturalFitnessInfoAnchor(event.currentTarget)}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                  <Popover
                    open={Boolean(architecturalFitnessInfoAnchor)}
                    anchorEl={architecturalFitnessInfoAnchor}
                    onClose={() => setArchitecturalFitnessInfoAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <Typography style={{ padding: 16, maxWidth: 320 }}>
                      How closely the component behaves as declared in its architectural specifications.
                    </Typography>
                  </Popover>
                </Box>
                <Typography variant="h4">
                  {agenticEvaluator.architecturalFitness !== null
                    ? `${agenticEvaluator.architecturalFitness.toFixed(1)}%`
                    : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography gutterBottom>Documentation Score</Typography>
                  <IconButton
                    aria-label="More information about documentation score"
                    size="small"
                    onClick={(event) => setDocumentationScoreInfoAnchor(event.currentTarget)}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                  <Popover
                    open={Boolean(documentationScoreInfoAnchor)}
                    anchorEl={documentationScoreInfoAnchor}
                    onClose={() => setDocumentationScoreInfoAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <Typography style={{ padding: 16, maxWidth: 320 }}>
                      How well the project repository is documented.
                    </Typography>
                  </Popover>
                </Box>
                <Typography variant="h4">
                  {agenticEvaluator.documentationScore !== null
                    ? `${agenticEvaluator.documentationScore.toFixed(1)}%`
                    : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {agenticEvaluator.weakPointCategories.length > 0 ? (
          <Grid item xs={12} sm={6} md={8}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ marginBottom: 16 }}>
                  <Typography gutterBottom>Architectural Findings</Typography>
                  <IconButton
                    aria-label="More information about architectural findings"
                    size="small"
                    onClick={(event) => setWeakPointsInfoAnchor(event.currentTarget)}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                  <Popover
                    open={Boolean(weakPointsInfoAnchor)}
                    anchorEl={weakPointsInfoAnchor}
                    onClose={() => setWeakPointsInfoAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <Typography style={{ padding: 16, maxWidth: 320 }}>
                      Architectural findings highlight areas for improvement identified by the architectural
                      analysis. Colors indicate the severity level: high, medium, or low.
                    </Typography>
                  </Popover>
                </Box>
                <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginBottom: 16 }}>
                  {WEAK_POINT_SEVERITY_ORDER.map((severity) => (
                    <Box key={severity} display="flex" alignItems="center" mr={2}>
                      <Box
                        width={12}
                        height={12}
                        mr={1}
                        borderRadius={2}
                        style={{ backgroundColor: WEAK_POINT_SEVERITY_COLOR[severity] }}
                      />
                      <Typography variant="caption" color="textSecondary">
                        {WEAK_POINT_SEVERITY_LABEL[severity]}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
                  {agenticEvaluator.weakPointCategories.map(({ category, label, total, severities }) => (
                    <Box key={category}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0}>
                        <Typography variant="body1">{label}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {severities
                            .filter(({ count }) => count > 0)
                            .map(({ severity, count }) => `${WEAK_POINT_SEVERITY_LABEL[severity]}: ${count}`)
                            .join(', ')}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                        <Box
                          display="flex"
                          flexGrow={1}
                          height={18}
                          borderRadius={9}
                          overflow="hidden"
                          style={{ backgroundColor: '#eceff1' }}
                        >
                          {severities.map(({ severity, count }) => {
                            if (count === 0 || total === 0) {
                              return null;
                            }

                            return (
                              <Tooltip
                                key={`${category}-${severity}`}
                                title={`${label} — ${WEAK_POINT_SEVERITY_LABEL[severity]}: ${count}`}
                              >
                                <Box
                                  height="100%"
                                  style={{
                                    width: `${(count / total) * 100}%`,
                                    backgroundColor: WEAK_POINT_SEVERITY_COLOR[severity],
                                  }}
                                />
                              </Tooltip>
                            );
                          })}
                        </Box>
                        <Typography variant="h4">{total}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <Grid item xs={12} sm={6} md={8}>
            <Card>
              <CardContent>
                <Typography gutterBottom>Architectural Findings</Typography>
                <Typography variant="h4">N/A</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ProjectMetricsDisplay;
